import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Clapperboard, Clock, X, ExternalLink, ChevronLeft, ChevronRight, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import {
  DIFFICULTY, PROVIDERS, playerUrl, platformUrl, GUIDES_THUMBNAIL_FALLBACK,
} from '../data/guides';

const DISCORD_URL = 'https://discord.com/invite/RwpMfRdzex';

// Aviso previo: el reproductor de la plataforma no se carga hasta que el jugador acepta.
// La aceptación dura la visita; con «Recordar» se guarda en este navegador (localStorage).
const CONSENT_KEY = 'fb-video-consent';
const sessionConsent = new Set();

function readSavedConsent() {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function hasConsent(provider) {
  return sessionConsent.has(provider) || readSavedConsent()[provider] === true;
}

function giveConsent(provider, remember) {
  sessionConsent.add(provider);
  if (!remember) return;
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...readSavedConsent(), [provider]: true }));
  } catch {
    /* sin almacenamiento: vale para esta visita */
  }
}

// Errores de onPlayerError que significan que el vídeo no se puede ver aquí
// (1001 INVALID_VIDEO: borrado, privado o no disponible; 2001 SERVER_ERROR).
// No hay detección de bloqueo por tiempo: con autoplay=0 el Embed Player no envía
// onPlayerReady hasta que se pulsa play (comprobado el 2026-10-30), así que el enlace
// «Ver en TikTok» queda siempre visible bajo el reproductor.
const TIKTOK_UNAVAILABLE_CODES = [1001, 2001];

export function DifficultyBadge({ level }) {
  const cfg = DIFFICULTY[level] ?? DIFFICULTY.basic;
  return (
    <span
      className="difficulty-badge"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

function PlatformLink({ guide, className = 'video-platform-link' }) {
  const href = platformUrl(guide);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      <ExternalLink size={13} />
      Ver en {PROVIDERS[guide.provider].label}
    </a>
  );
}

function VideoPlayer({ guide }) {
  const providerLabel = PROVIDERS[guide.provider].label;
  const [phase, setPhase] = useState(() => (hasConsent(guide.provider) ? 'player' : 'notice'));
  const [remember, setRemember] = useState(false);
  const [thumbErrored, setThumbErrored] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (phase !== 'player' || guide.provider !== 'tiktok') return undefined;

    const onMessage = (e) => {
      if (e.origin !== 'https://www.tiktok.com') return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const msg = e.data;
      if (!msg || msg['x-tiktok-player'] !== true) return;
      if (msg.type === 'onPlayerError' && TIKTOK_UNAVAILABLE_CODES.includes(msg.value?.errorCode)) {
        setPhase('error');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [phase, guide.provider]);

  const accept = () => {
    giveConsent(guide.provider, remember);
    setPhase('player');
  };

  return (
    <div className={`video-player video-player--${guide.format}`} data-testid="video-player">
      {phase === 'notice' && (
        <div className="video-notice" data-testid="video-notice">
          <img
            className="video-notice-bg"
            src={thumbErrored ? GUIDES_THUMBNAIL_FALLBACK : guide.thumbnail}
            alt=""
            aria-hidden="true"
            onError={() => setThumbErrored(true)}
          />
          <div className="video-notice-body">
            <ShieldCheck size={30} className="video-notice-icon" />
            <p className="video-notice-title">Este vídeo se carga desde {providerLabel}</p>
            <p className="video-notice-text">
              Al reproducirlo, {providerLabel} puede recibir tu dirección IP y guardar cookies en
              tu navegador. Más información en la{' '}
              <Link to="/privacidad" className="cyan-highlight">política de privacidad</Link>.
            </p>
            <label className="video-notice-remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              Recordar mi elección en este navegador
            </label>
            <button
              type="button"
              className="video-notice-cta"
              onClick={accept}
              data-testid="video-notice-accept"
            >
              CARGAR VÍDEO
            </button>
            <PlatformLink guide={guide} />
          </div>
        </div>
      )}

      {phase === 'player' && (
        <iframe
          ref={iframeRef}
          src={playerUrl(guide)}
          title={guide.title}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      )}

      {phase === 'error' && (
        <div className="video-error" role="alert" data-testid="video-error">
          <AlertTriangle size={30} className="video-error-icon" />
          <p className="video-error-title">VÍDEO NO DISPONIBLE</p>
          <p className="video-error-text">
            Este vídeo no se puede ver ahora mismo desde la web. Prueba a abrirlo en {providerLabel}.
          </p>
          <PlatformLink guide={guide} className="video-notice-cta" />
        </div>
      )}
    </div>
  );
}

function ComingSoon({ guide }) {
  return (
    <div className={`video-modal-soon video-modal-soon--${guide.format}`}>
      <div className="video-modal-soon-icon">
        <Clapperboard size={42} />
      </div>
      <p className="video-modal-soon-eyebrow">PRÓXIMAMENTE</p>
      <p className="video-modal-soon-title">{guide.title}</p>
      <p className="video-modal-soon-desc">
        Estamos preparando esta guía. Únete al Discord y te avisaremos en cuanto esté disponible.
      </p>
      <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="video-modal-soon-cta">
        <ExternalLink size={13} />
        ÚNETE AL DISCORD
      </a>
    </div>
  );
}

// Modal único de vídeo: YouTube o TikTok, 16:9 o 9:16. onPrev/onNext (opcionales) recorren
// una lista; null deja el botón desactivado. El iframe se desmonta al cerrar o cambiar de vídeo.
export default function VideoModal({ guide, onClose, onPrev, onNext }) {
  // Radix devuelve el foco a su Dialog.Trigger; aquí se abre desde cualquier tarjeta,
  // así que se guarda el elemento que tenía el foco al abrir y se le devuelve al cerrar.
  const [returnFocusEl] = useState(() => document.activeElement);
  if (!guide) return null;
  const hasNav = onPrev !== undefined || onNext !== undefined;

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft' && onPrev) onPrev();
    if (e.key === 'ArrowRight' && onNext) onNext();
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="video-modal-backdrop" data-testid="video-modal">
          <DialogPrimitive.Content
            className={`video-modal video-modal--${guide.format}`}
            onKeyDown={onKeyDown}
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              returnFocusEl?.focus?.();
            }}
          >
            <DialogPrimitive.Close asChild>
              <button type="button" className="video-modal-close" aria-label="Cerrar">
                <X size={18} />
              </button>
            </DialogPrimitive.Close>

            {guide.videoId ? <VideoPlayer key={guide.id} guide={guide} /> : <ComingSoon guide={guide} />}

            <div className="video-modal-meta">
              <span className="video-modal-meta-eyebrow">
                {guide.format === 'vertical' ? 'GUÍA RÁPIDA' : 'GUÍA EN VÍDEO'} · {PROVIDERS[guide.provider].label.toUpperCase()}
              </span>
              <DialogPrimitive.Title className="video-modal-meta-title">{guide.title}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="video-modal-meta-desc">
                {guide.description}
              </DialogPrimitive.Description>
              <div className="video-modal-meta-row">
                {guide.duration && <span className="meta-chip"><Clock size={12} /> {guide.duration}</span>}
                <DifficultyBadge level={guide.difficulty} />
              </div>
              {guide.videoId && (
                <span className="video-modal-meta-hint">
                  ¿No se ve? <PlatformLink guide={guide} />
                </span>
              )}
              {hasNav && (
                <div className="video-modal-nav">
                  <button
                    type="button"
                    className="video-modal-nav-btn"
                    onClick={onPrev ?? undefined}
                    disabled={!onPrev}
                    aria-label="Guía anterior"
                    data-testid="video-modal-prev"
                  >
                    <ChevronLeft size={15} /> ANTERIOR
                  </button>
                  <button
                    type="button"
                    className="video-modal-nav-btn video-modal-nav-btn--primary"
                    onClick={onNext ?? undefined}
                    disabled={!onNext}
                    aria-label="Guía siguiente"
                    data-testid="video-modal-next"
                  >
                    SIGUIENTE <ChevronRight size={15} />
                  </button>
                </div>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
